import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapCanvas from '../map/MapCanvas.jsx';
import Modal from '../components/Modal.jsx';
import GearSummary from '../components/GearSummary.jsx';
import RideCard from '../components/RideCard.jsx';
import RideForm from '../components/RideForm.jsx';
import JoinForm from '../components/JoinForm.jsx';
import ShareBox from '../components/ShareBox.jsx';
import TripSettings from '../components/TripSettings.jsx';
import IdentityGate from '../components/IdentityGate.jsx';
import { useToast } from '../components/Toast.jsx';
import { api } from '../lib/api.js';
import { colorMap, legShades } from '../lib/colors.js';
import { formatDay, formatTime, initials, relativeDay } from '../lib/format.js';
import { rememberMember, setDisplayName } from '../lib/identity.js';
import { navigate } from '../lib/router.js';
import { useTrip } from '../lib/useTrip.js';

const DEST_MARKER = { kind: 'dest', glyph: '🏔' };

/** True on the layout where the panel is a bottom sheet over the map. */
const isCompactLayout = () => window.matchMedia('(max-width: 900px)').matches;

export default function TripPage({ shareToken, meta }) {
  const { trip, status, error, live, identity, refreshIdentity, applyTrip } = useTrip(shareToken);
  const toast = useToast();

  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, ride?}
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  /**
   * On a phone the sheet and the map share one screen, so working on one means
   * wanting less of the other. A tap on the map folds the sheet away; a tap on
   * the folded sheet brings it back. Nothing happens on a wide screen, where the
   * panel sits beside the map and never competes with it.
   */
  const giveMapRoom = useCallback(() => {
    if (isCompactLayout()) setCollapsed(true);
  }, []);
  const [hiddenRides, setHiddenRides] = useState(() => new Set());

  /* -- draft itinerary preview while a ride is being written -------------- */
  const [draft, setDraft] = useState(null);
  const [draftRoute, setDraftRoute] = useState(null);
  const onDraftChange = useCallback((next) => setDraft(next), []);

  const draftWaypoints = useMemo(() => {
    if (!draft || !trip) return [];
    return [draft.origin, ...(draft.stops ?? []), trip.destination]
      .filter((p) => p && p.lat != null && p.lng != null)
      .map((p) => [p.lng, p.lat]);
  }, [draft, trip]);

  const draftKey = draftWaypoints.map((w) => w.join(',')).join('|');

  useEffect(() => {
    if (draftWaypoints.length < 2) {
      setDraftRoute(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .previewRoute(draftWaypoints, controller.signal)
        .then(({ route }) => setDraftRoute(route))
        .catch(() => setDraftRoute(null));
    }, 600);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  /* -- derived ------------------------------------------------------------ */
  const rides = trip?.rides ?? [];
  const colors = useMemo(() => colorMap(rides), [rides]);
  const isAdmin = Boolean(identity.adminToken);

  /** The participant this browser is acting as, resolved against the live roster. */
  const me = useMemo(
    () => (trip?.members ?? []).find((m) => m.id === identity.memberId) ?? null,
    [trip, identity.memberId],
  );
  const myRideIds = useMemo(
    () => new Set(rides.filter((r) => r.driverMemberId === me?.id).map((r) => r.id)),
    [rides, me],
  );
  const myPassengerIds = useMemo(
    () =>
      new Set(
        rides.flatMap((r) => r.passengers.filter((p) => p.memberId === me?.id).map((p) => p.id)),
      ),
    [rides, me],
  );

  // Ask who this is the first time the outing is opened on a device. An id kept
  // from a deleted member is stale, so that reopens the picker too.
  useEffect(() => {
    if (status !== 'ready') return;
    if (!identity.memberId || !me) setDialog((d) => d ?? { type: 'identity' });
  }, [status, identity.memberId, me]);

  const visible = (rideId) => !hiddenRides.has(rideId);

  const markers = useMemo(() => {
    if (!trip) return [];
    const out = [];
    if (trip.destination?.lat != null) {
      out.push({
        id: 'destination',
        lat: trip.destination.lat,
        lng: trip.destination.lng,
        label: trip.destination.label,
        place: trip.destination,
        signature: `dest:${trip.destination.lat}:${trip.destination.lng}`,
        ...DEST_MARKER,
      });
    }
    if (trip.meetingPoint?.lat != null) {
      out.push({
        id: 'meeting',
        kind: 'meet',
        glyph: '🅿',
        lat: trip.meetingPoint.lat,
        lng: trip.meetingPoint.lng,
        label: trip.meetingPoint.label,
        place: trip.meetingPoint,
        signature: `meet:${trip.meetingPoint.lat}:${trip.meetingPoint.lng}`,
      });
    }

    for (const ride of rides) {
      if (!visible(ride.id)) continue;
      const color = colors.get(ride.id);
      const dimmed = Boolean(selectedId) && selectedId !== ride.id;
      if (ride.origin?.lat != null) {
        out.push({
          id: `o-${ride.id}`,
          kind: 'origin',
          color,
          glyph: ride.driver.name.slice(0, 1).toUpperCase(),
          lat: ride.origin.lat,
          lng: ride.origin.lng,
          label: selectedId === ride.id ? ride.origin.label : null,
          place: ride.origin,
          dimmed,
          rideId: ride.id,
          signature: `o:${color}:${dimmed}:${selectedId === ride.id}`,
        });
      }
      ride.stops.forEach((stop, i) => {
        if (stop.place?.lat == null) return;
        out.push({
          id: `s-${stop.id}`,
          kind: 'stop',
          color,
          glyph: String(i + 1),
          lat: stop.place.lat,
          lng: stop.place.lng,
          label: selectedId === ride.id ? stop.place.label : null,
          place: stop.place,
          dimmed,
          rideId: ride.id,
          signature: `s:${color}:${dimmed}:${selectedId === ride.id}`,
        });
      });
    }

    return out;
  }, [trip, rides, colors, selectedId, hiddenRides]);

  const routes = useMemo(() => {
    if (!trip) return [];
    const out = [];
    for (const ride of rides) {
      if (!visible(ride.id)) continue;
      const color = colors.get(ride.id);
      const dimmed = Boolean(selectedId) && selectedId !== ride.id;

      if (ride.route?.coordinates?.length) {
        const points = ride.route.coordinates.map(([lng, lat]) => [lat, lng]);

        /**
         * Cut the line at official stops only, and shade each leg.
         *
         * Via points are skipped deliberately: they bend the road, they are not a
         * stage of the journey, so giving them a colour break would invent stops
         * that nobody is waiting at.
         */
        const cuts = [0];
        (ride.route.waypoints ?? []).forEach((w, i) => {
          if (w.kind === 'stop') {
            const at = ride.route.waypointIndexes?.[i];
            if (at != null && at > 0 && at < points.length - 1) cuts.push(at);
          }
        });
        cuts.push(points.length - 1);
        const bounds = [...new Set(cuts)].sort((a, b) => a - b);
        const shades = legShades(color, bounds.length - 1);

        for (let k = 0; k < bounds.length - 1; k += 1) {
          out.push({
            id: `r-${ride.id}-${k}`,
            color: shades[k],
            points: points.slice(bounds[k], bounds[k + 1] + 1),
            selected: selectedId === ride.id,
            dimmed,
            rideId: ride.id,
          });
        }
      } else {
        // Routing not back yet (or unavailable): straight lines still convey the plan.
        const pts = [ride.origin, ...ride.stops.map((s) => s.place), trip.destination]
          .filter((p) => p?.lat != null)
          .map((p) => [p.lat, p.lng]);
        if (pts.length > 1) {
          out.push({
            id: `r-${ride.id}`,
            color,
            points: pts,
            dashed: true,
            selected: selectedId === ride.id,
            dimmed,
            rideId: ride.id,
          });
        }
      }
    }

    if (draftRoute?.coordinates?.length) {
      out.push({
        id: 'draft-route',
        color: '#ff7a45',
        points: draftRoute.coordinates.map(([lng, lat]) => [lat, lng]),
        selected: true,
        dashed: true,
      });
    }
    return out;
  }, [trip, rides, colors, selectedId, hiddenRides, draftRoute]);

  /** Shown faintly on every place map, so a point is placed in context. */
  const placeContext = useMemo(() => {
    if (!trip) return [];
    const out = [];
    if (trip.destination?.lat != null) {
      out.push({ id: 'dest', kind: 'dest', glyph: '🏔', lat: trip.destination.lat, lng: trip.destination.lng,
                 label: trip.destination.label, place: trip.destination });
    }
    if (trip.meetingPoint?.lat != null) {
      out.push({ id: 'meet', kind: 'meet', glyph: '🅿', lat: trip.meetingPoint.lat, lng: trip.meetingPoint.lng,
                 label: trip.meetingPoint.label, place: trip.meetingPoint });
    }
    return out;
  }, [trip]);

  /** Direction arrows, from the full line so spacing does not restart at a stop. */
  const arrowRoutes = useMemo(() => {
    if (!trip) return [];
    return rides
      .filter((ride) => visible(ride.id) && ride.status !== 'cancelled')
      .filter((ride) => !selectedId || selectedId === ride.id)
      .map((ride) => {
        const color = colors.get(ride.id);
        const points = ride.route?.coordinates?.length
          ? ride.route.coordinates.map(([lng, lat]) => [lat, lng])
          : [ride.origin, ...ride.stops.map((s2) => s2.place), trip.destination]
              .filter((pt) => pt?.lat != null)
              .map((pt) => [pt.lat, pt.lng]);
        return { id: ride.id, color, points };
      })
      .filter((r) => r.points.length > 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip, rides, colors, selectedId, hiddenRides]);

  /* -- map framing -------------------------------------------------------- */
  const fitBounds = useMemo(() => {
    if (!trip) return null;
    const source = selectedId ? rides.filter((r) => r.id === selectedId) : rides;
    const pts = [];
    if (trip.destination?.lat != null) pts.push([trip.destination.lat, trip.destination.lng]);
    if (!selectedId && trip.meetingPoint?.lat != null) {
      pts.push([trip.meetingPoint.lat, trip.meetingPoint.lng]);
    }
    for (const ride of source) {
      if (ride.origin?.lat != null) pts.push([ride.origin.lat, ride.origin.lng]);
      for (const stop of ride.stops) if (stop.place?.lat != null) pts.push([stop.place.lat, stop.place.lng]);
    }
    return pts.length ? pts : null;
  }, [trip, rides, selectedId]);

  /**
   * Folding the sheet changes how much map is visible, so the route is re-framed
   * into it. The fit measures the panel, and the panel slides for a quarter of a
   * second, so it has to wait for that to finish: measuring mid-animation frames
   * the route for a layout that no longer exists a moment later.
   */
  const panelRef = useRef(null);

  /**
   * Pointing at a stop in a card moves the map onto it. The nonce is what makes
   * the map act: the same place clicked twice is still two requests to go there,
   * and the reader may have panned away in between.
   */
  const [focus, setFocus] = useState(null);
  const locate = useCallback(
    (place) => {
      if (place?.lat == null || place?.lng == null) return;
      const go = () => setFocus({ lat: place.lat, lng: place.lng, zoom: 15, nonce: Date.now() });
      // On a phone the sheet folds away first to uncover the map. The map aims at
      // whatever the sheet leaves visible, so it has to measure a settled layout,
      // not one a quarter of a second into its slide.
      if (isCompactLayout()) {
        giveMapRoom();
        const timer = setTimeout(go, 330);
        return () => clearTimeout(timer);
      }
      go();
      return undefined;
    },
    [giveMapRoom],
  );

  /**
   * The sheet scrolls as a whole now, so folding it while the reader is halfway
   * down would leave that spot showing in the strip that stays visible, instead
   * of the title and the seat count the strip is there to give.
   */
  useEffect(() => {
    if (collapsed) panelRef.current?.scrollTo({ top: 0 });
  }, [collapsed]);

  const [layoutTick, setLayoutTick] = useState(0);
  const firstLayout = useRef(true);
  useEffect(() => {
    if (firstLayout.current) {
      firstLayout.current = false;
      return undefined;
    }
    const timer = setTimeout(() => setLayoutTick((n) => n + 1), 320);
    return () => clearTimeout(timer);
  }, [collapsed]);

  const fitKey = `${trip?.id ?? ''}:${selectedId ?? 'all'}:${rides.length}:${layoutTick}`;

  /* -- actions ------------------------------------------------------------ */
  const shareUrl = `${window.location.origin}/t/${shareToken}`;

  const closeDialog = () => {
    setDialog(null);
    setFormError(null);
    setDraft(null);
    setDraftRoute(null);
  };

  const run = async (fn, successMessage) => {
    setBusy(true);
    setFormError(null);
    try {
      const result = await fn();
      if (successMessage) toast.success(successMessage);
      return result;
    } catch (err) {
      setFormError(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  /** Claim or switch identity. The server resolves an existing name to its member. */
  const chooseIdentity = async ({ memberId, name }) => {
    try {
      if (memberId) {
        rememberMember(shareToken, memberId);
        if (name) setDisplayName(name);
        refreshIdentity();
        closeDialog();
        return;
      }
      const res = await run(() => api.claimMember(shareToken, { name }));
      rememberMember(shareToken, res.memberId);
      setDisplayName(name);
      refreshIdentity();
      applyTrip(res.trip);
      toast.success(res.claimed ? `Content de vous revoir, ${name}.` : `Bienvenue, ${name}.`);
      closeDialog();
    } catch {
      /* shown in the gate */
    }
  };

  const submitRide = async (payload) => {
    try {
      if (dialog.ride) {
        const res = await run(
          () =>
            api.updateRide(shareToken, dialog.ride.id, payload, {
              as: identity.memberId,
              adminToken: identity.adminToken,
            }),
          'Trajet mis a jour.',
        );
        applyTrip(res.trip);
      } else {
        const res = await run(
          () => api.createRide(shareToken, payload, { as: identity.memberId }),
          'Votre voiture est ajoutee.',
        );
        applyTrip(res.trip);
        setSelectedId(res.rideId);
      }
      closeDialog();
    } catch {
      /* message already surfaced in the form */
    }
  };

  const submitJoin = async (payload) => {
    try {
      const res = await run(
        () => api.joinRide(shareToken, dialog.ride.id, payload, { as: identity.memberId }),
        'Place reservee.',
      );
      applyTrip(res.trip);
      closeDialog();
    } catch {
      /* shown in the form */
    }
  };

  const submitSettings = async (patch) => {
    try {
      const res = await run(
        () => api.updateTrip(shareToken, identity.adminToken, patch),
        'Sortie mise a jour.',
      );
      applyTrip(res.trip);
      closeDialog();
    } catch {
      /* shown in the form */
    }
  };

  const deleteRide = async (ride) => {
    if (!window.confirm(`Supprimer la voiture de ${ride.driver.name} ?`)) return;
    try {
      const res = await run(
        () =>
          api.deleteRide(shareToken, ride.id, {
            as: identity.memberId,
            adminToken: identity.adminToken,
          }),
        'Trajet supprime.',
      );
      applyTrip(res.trip);
      setSelectedId(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const leaveRide = async (ride, passenger) => {
    const mine = myPassengerIds.has(passenger.id);
    if (!window.confirm(mine ? 'Liberer votre place ?' : `Retirer ${passenger.name} ?`)) return;
    try {
      const res = await run(
        () =>
          api.leaveRide(shareToken, ride.id, passenger.id, {
            as: identity.memberId,
            adminToken: identity.adminToken,
          }),
        mine ? 'Place liberee.' : 'Passager retire.',
      );
      applyTrip(res.trip);
    } catch (err) {
      toast.error(err.message);
    }
  };

  /**
   * A drag finished: store the new shaping points. The road itself is recomputed
   * server-side and arrives over the live channel, so there is nothing to await
   * here beyond the write.
   */
  const commitVia = useCallback(
    async (rideId, via) => {
      try {
        const res = await api.setRideVia(shareToken, rideId, via, {
          as: identity.memberId,
          adminToken: identity.adminToken,
        });
        applyTrip(res.trip);
      } catch (err) {
        toast.error(err.message);
      }
    },
    [shareToken, identity.memberId, identity.adminToken, applyTrip, toast],
  );

  /**
   * Your own car's road is always draggable: no mode to turn on, and nothing to
   * discover. It is withdrawn only while another ride is singled out, where the
   * handles would sit on a line that is deliberately dimmed.
   */
  const myRide = useMemo(() => {
    const mine = rides.find((r) => myRideIds.has(r.id));
    if (!mine || mine.status === 'cancelled') return null;
    if (hiddenRides.has(mine.id)) return null;
    if (selectedId && selectedId !== mine.id) return null;
    return mine;
  }, [rides, myRideIds, hiddenRides, selectedId]);

  // Suppressed while a point is being placed on the map: that gesture owns the
  // pointer, and two things claiming the same drag is how a map feels broken.
  const routeEditor = useMemo(() => {
    const route = myRide?.route;
    if (!myRide || !route?.coordinates?.length || !route.waypoints?.length) return null;
    return {
      rideId: myRide.id,
      color: colors.get(myRide.id),
      points: route.coordinates.map(([lng, lat]) => [lat, lng]),
      waypoints: route.waypoints,
      waypointIndexes: route.waypointIndexes ?? [],
      via: myRide.via ?? [],
      onCommit: (next) => commitVia(myRide.id, next),
    };
  }, [myRide, colors, commitVia]);

  const toggleRideVisible = (rideId) =>
    setHiddenRides((set) => {
      const next = new Set(set);
      if (next.has(rideId)) next.delete(rideId);
      else next.add(rideId);
      return next;
    });

  /* -- render ------------------------------------------------------------- */

  if (status === 'error') {
    return (
      <div className="landing">
        <div className="landing-inner card" style={{ textAlign: 'center' }}>
          <div className="empty-mark">🧗</div>
          <h1 style={{ fontSize: 21, marginTop: 10 }}>Sortie introuvable</h1>
          <p className="muted" style={{ marginTop: 10 }}>{error?.message}</p>
          <button type="button" className="btn btn-primary" style={{ marginTop: 18 }} onClick={() => navigate('/')}>
            Retour a l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <MapCanvas
        markers={markers}
        routes={routes}
        fitKey={fitKey}
        fitBounds={fitBounds}
        tiles={meta?.tiles}
        framedByPanel
        routeEditor={routeEditor}
        arrowRoutes={arrowRoutes}
        onBackgroundTap={giveMapRoom}
        focusOn={focus}
        onSelect={(id) => {
          setSelectedId(id);
          setCollapsed(false);
        }}
      />

      <div className="topbar">
        <div className="float-card row gap-6">
          <span className="row gap-6" style={{ padding: '0 8px' }} title={live ? 'Mises a jour en direct' : 'Hors ligne'}>
            <span className={`live-dot ${live ? 'on' : 'off'}`} />
            <span className="small muted">{live ? 'en direct' : 'hors ligne'}</span>
          </span>
          {isAdmin && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDialog({ type: 'settings' })}>
              Reglages
            </button>
          )}
          <button type="button" className="btn btn-sm" onClick={() => setDialog({ type: 'share' })}>
            Partager
          </button>
        </div>
      </div>

      {rides.length > 1 && (
        <div className="legend">
          <div className="section-label">Voitures</div>
          {rides.map((ride) => (
            <div
              key={ride.id}
              className={`legend-row ${hiddenRides.has(ride.id) ? 'dimmed' : ''}`}
              onClick={() => toggleRideVisible(ride.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleRideVisible(ride.id)}
              title="Afficher / masquer cet itineraire"
            >
              <span className="swatch" style={{ background: colors.get(ride.id) }} />
              <span className="truncate" style={{ flex: 1 }}>{ride.driver.name}</span>
              <span className="small faint tabular">{ride.seats.seatsFree}</span>
            </div>
          ))}
        </div>
      )}

      <aside
        ref={panelRef}
        className={`panel ${collapsed ? 'collapsed' : ''}`}
        /* Folded, the whole sheet is the way back: aiming for the grip alone is
           needless precision when the panel is doing nothing else. */
        onClick={collapsed ? () => setCollapsed(false) : undefined}
        aria-expanded={!collapsed}
      >
        <button
          type="button"
          className="panel-grip"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((c) => !c);
          }}
          aria-label={collapsed ? 'Ouvrir le panneau' : 'Reduire le panneau'}
        />

        {/* The strip left visible when folded: the two things worth a glance. */}
        {status === 'ready' && (
          <div className="panel-peek">
            <span className="truncate" style={{ flex: 1, fontWeight: 650 }}>{trip.title}</span>
            <span className={`chip ${trip.totals.seatsFree > 0 ? 'chip-ok' : 'chip-warn'}`}>
              {trip.totals.seatsFree > 0 ? `${trip.totals.seatsFree} libres` : 'complet'}
            </span>
            <span className="chip">{trip.totals.cars} 🚗</span>
          </div>
        )}

        <header className="panel-head">
          <div className="row gap-8">
            <button type="button" className="brand btn btn-ghost btn-sm" onClick={() => navigate('/')} style={{ padding: '2px 6px' }}>
              <span className="brand-mark">🧗</span>
              <span className="small">Covoiturage</span>
            </button>
            <div className="spacer" />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setCollapsed((c) => !c)}
              style={{ display: 'none' }}
            >
              {collapsed ? 'Ouvrir' : 'Reduire'}
            </button>
          </div>

          {status === 'ready' && (
            <button
              type="button"
              className="whoami"
              style={{ marginTop: 10 }}
              onClick={() => setDialog({ type: 'identity' })}
              title="Changer de participant"
            >
              {me ? (
                <>
                  <span className="whoami-avatar">{initials(me.name)}</span>
                  <span className="small" style={{ flex: 1, minWidth: 0 }}>
                    <span className="truncate" style={{ display: 'block', fontWeight: 600 }}>{me.name}</span>
                  </span>
                  <span className="small faint">Ce n'est pas vous ?</span>
                </>
              ) : (
                <>
                  <span className="whoami-avatar">?</span>
                  <span className="small" style={{ flex: 1 }}>Dites qui vous etes</span>
                  <span className="small faint">Choisir</span>
                </>
              )}
            </button>
          )}

          {status === 'loading' ? (
            <div className="stack gap-8" style={{ marginTop: 12 }}>
              <div className="skeleton" style={{ height: 22, width: '70%' }} />
              <div className="skeleton" style={{ height: 14, width: '45%' }} />
            </div>
          ) : (
            <>
              <h1 className="trip-title">{trip.title}</h1>
              <div className="trip-meta">
                <span className="chip chip-accent">🏔 {trip.destination.label}</span>
                <span className="chip">📅 {formatDay(trip.departureDate)}</span>
                <span className="chip">{relativeDay(trip.departureDate)}</span>
                {trip.meetingPoint && (
                  <span className="chip">
                    🅿 RDV {trip.meetingPoint.label}
                    {trip.meetingTime ? ` · ${formatTime(trip.meetingTime)}` : ''}
                  </span>
                )}
              </div>
              {trip.description && <p className="small muted" style={{ marginTop: 10 }}>{trip.description}</p>}
              <div className="totals" style={{ marginTop: 12 }}>
                <div className="total-cell">
                  <div className="total-val">{trip.totals.cars}</div>
                  <div className="total-lbl">voitures</div>
                </div>
                <div className="total-cell">
                  <div className="total-val">{trip.totals.people}</div>
                  <div className="total-lbl">personnes</div>
                </div>
                <div className={`total-cell total-free ${trip.totals.seatsFree === 0 ? 'is-zero' : ''}`}>
                  <div className="total-val">{trip.totals.seatsFree}</div>
                  <div className="total-lbl">libres</div>
                </div>
                <div className="total-cell">
                  <div className="total-val">{trip.totals.seatsOffered}</div>
                  <div className="total-lbl">proposees</div>
                </div>
              </div>
            </>
          )}
        </header>

        <div className="panel-body">
          {status === 'loading' && (
            <div className="stack gap-10">
              {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 82 }} />)}
            </div>
          )}

          {status === 'ready' && rides.length === 0 && (
            <div className="empty">
              <div className="empty-mark">🚗</div>
              <p style={{ marginTop: 10, fontWeight: 600, color: 'var(--text-dim)' }}>Aucune voiture pour l'instant.</p>
              <p className="small" style={{ marginTop: 6 }}>
                Proposez la votre, ou partagez le lien au club pour que chacun ajoute la sienne.
              </p>
            </div>
          )}

          {status === 'ready' && rides.length > 0 && <GearSummary rides={rides} />}

          {status === 'ready' &&
            rides.map((ride) => (
              <RideCard
                key={ride.id}
                ride={ride}
                color={colors.get(ride.id)}
                destination={trip.destination}
                selected={selectedId === ride.id}
                isMine={myRideIds.has(ride.id)}
                canManage={myRideIds.has(ride.id) || isAdmin}
                myPassengerIds={myPassengerIds}
                onSelect={() => setSelectedId((id) => (id === ride.id ? null : ride.id))}
                onJoin={() => setDialog(me ? { type: 'join', ride } : { type: 'identity' })}
                onEdit={() => setDialog({ type: 'ride', ride })}
                onDelete={() => deleteRide(ride)}
                canShapeRoute={myRide?.id === ride.id}
                onResetRoute={() => commitVia(ride.id, [])}
                onLeave={(passenger) => leaveRide(ride, passenger)}
                onLocate={locate}
              />
            ))}
        </div>

        <footer className="panel-foot">
          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => setDialog(me ? { type: 'ride', ride: null } : { type: 'identity' })}
            disabled={status !== 'ready'}
          >
            🚗 Proposer ma voiture
          </button>
        </footer>
      </aside>

      {dialog?.type === 'ride' && (
        <Modal
          title={dialog.ride ? 'Modifier mon trajet' : 'Proposer ma voiture'}
          subtitle={trip ? `${trip.title} · ${formatDay(trip.departureDate)}` : ''}
          onClose={closeDialog}
          wide
        >
          <RideForm
            ride={dialog.ride}
            me={me}
            defaultDeparture={trip?.departureDate}
            gearSuggestions={meta.gearSuggestions}
            mapContext={placeContext}
            tiles={meta?.tiles}
            onDraftChange={onDraftChange}
            onSubmit={submitRide}
            onCancel={closeDialog}
            busy={busy}
            error={formError}
          />
        </Modal>
      )}

      {dialog?.type === 'join' && (
        <Modal title="Prendre une place" subtitle={`Voiture de ${dialog.ride.driver.name}`} onClose={closeDialog}>
          <JoinForm
            ride={rides.find((r) => r.id === dialog.ride.id) ?? dialog.ride}
            me={me}
            color={colors.get(dialog.ride.id)}
            gearSuggestions={meta.gearSuggestions}
            onSubmit={submitJoin}
            onCancel={closeDialog}
            busy={busy}
            error={formError}
          />
        </Modal>
      )}

      {dialog?.type === 'settings' && (
        <Modal title="Reglages de la sortie" subtitle="Visible uniquement par l'organisateur" onClose={closeDialog} wide>
          <TripSettings
            trip={trip}
            mapContext={placeContext}
            tiles={meta?.tiles}
            onSubmit={submitSettings}
            onCancel={closeDialog}
            busy={busy}
            error={formError}
          />
        </Modal>
      )}

      {dialog?.type === 'identity' && trip && (
        <Modal
          title="Qui etes-vous ?"
          subtitle={trip.title}
          onClose={() => (me ? closeDialog() : setDialog(null))}
        >
          <IdentityGate
            trip={trip}
            current={identity.memberId}
            onChoose={chooseIdentity}
            onCancel={() => setDialog(null)}
            busy={busy}
            error={formError}
          />
        </Modal>
      )}

      {dialog?.type === 'share' && (
        <Modal title="Partager cette sortie" onClose={closeDialog}>
          <div className="stack gap-16">
            <p className="muted">
              Envoyez ce lien au club. Chacun pourra ajouter sa voiture ou reserver une place, sans compte
              ni inscription.
            </p>
            <ShareBox url={shareUrl} />
            {isAdmin && (
              <div className="card" style={{ background: 'var(--surface-2)' }}>
                <div className="section-label">Vous etes l'organisateur</div>
                <p className="small muted">
                  Ce navigateur detient le jeton d'organisateur. Il vous permet de modifier la sortie et de
                  retirer n'importe quelle voiture. Il n'est pas transmis dans le lien de partage.
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
